import { useEffect, useState } from 'react';
import { coworkerApi } from '@/lib/api/coworker';
import { useAuthStore, useCoworkerStore } from '@/store';
import useMemoryStore from '@/store/memory';

const CACHE_TTL_MS = 5 * 60 * 1000;
const MEMORY_REFRESH_DEBOUNCE_MS = 1000;
const cache = new Map();
const pending = new Map();

function cacheKey(userId, collectionId) {
  return `v7:${userId || ''}:${collectionId || ''}`;
}

function readCache(key) {
  const hit = cache.get(key);
  if (!hit) return undefined;
  if (hit.revision !== useMemoryStore.getState().workStarterPromptsRevision || Date.now() - hit.at > CACHE_TTL_MS) {
    cache.delete(key);
    return undefined;
  }
  return hit.items;
}

/**
 * Load memory-personalized Work composer pills written by Arya.
 * Returns null while loading or when Memory is off / empty so the default
 * capability pills stay up.
 */
export default function useWorkStarterPrompts({
  collectionId,
  enabled = true,
} = {}) {
  const token = useAuthStore((s) => s.token);
  const userId = useAuthStore((s) => s.user?.id);
  const memoryEnabled = useAuthStore((s) => s.user?.preferences?.memory?.isEnabled !== false);
  const includeMemory = useCoworkerStore((s) => s.composerIncludeMemory);
  const revision = useMemoryStore((s) => s.workStarterPromptsRevision);
  const key = cacheKey(userId, collectionId);
  const [items, setItems] = useState(() => (enabled ? readCache(key) ?? null : null));

  const canPersonalize = Boolean(enabled && token && userId && memoryEnabled && includeMemory);

  useEffect(() => {
    if (!canPersonalize) {
      setItems(null);
      return undefined;
    }

    const cached = readCache(key);
    if (cached !== undefined) {
      setItems(cached);
      return undefined;
    }

    let cancelled = false;
    setItems(null);
    const isCurrent = () => !cancelled
      && useMemoryStore.getState().workStarterPromptsRevision === revision;
    const load = () => {
      if (!isCurrent()) return;
      // Multiple composers can mount together; share one request per memory revision.
      const requestKey = `${key}:${revision}`;
      let request = pending.get(requestKey);
      if (!request) {
        request = coworkerApi.fetchStarterPrompts({ collectionId }).then((data) => {
          const next = data?.personalized && Array.isArray(data.suggestions) && data.suggestions.length
            ? data.suggestions
            : null;
          // A response started before a dream update must not restore stale cache entries.
          if (next && useMemoryStore.getState().workStarterPromptsRevision === revision) {
            cache.set(key, { at: Date.now(), items: next, revision });
          }
          return next;
        }).finally(() => pending.delete(requestKey));
        pending.set(requestKey, request);
      }
      request.then((next) => {
        if (isCurrent()) setItems(next);
      }).catch(() => {
        if (isCurrent()) setItems(null);
      });
    };

    // Dream commits arrive as a burst of memory and profile events after persistence.
    const changedAt = useMemoryStore.getState().workStarterPromptsChangedAt;
    const delay = Math.max(0, changedAt + MEMORY_REFRESH_DEBOUNCE_MS - Date.now());
    const timer = delay > 0 ? setTimeout(load, delay) : null;
    if (!delay) load();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [canPersonalize, collectionId, key, revision]);

  return canPersonalize ? items : null;
}
